// @ts-check

/** @file Tenant-operation-safe AwesomeTasks adapter for the public SyncClient. */

import SyncClient from "velocious/build/src/sync/sync-client.js"

/** @import {LocalMutationLogRecord} from "velocious/build/src/sync/local-mutation-log.js" */
/** @import {SyncReplayItem} from "velocious/build/src/sync/sync-api-client-types.js" */
/** @import {SyncClientOptions, SyncClientStopOptions} from "velocious/build/src/sync/sync-client-types.js" */

/** @typedef {SyncClientOptions & {flushMutationLog: () => Promise<void>, flushAppOfflineQueue?: () => Promise<number>}} ProjectSyncClientOptions */
/** @typedef {{record: LocalMutationLogRecord, resourceType: string, result: SyncReplayItem}} ConflictApply */
/** @typedef {{baseVersion?: string | number | null, data?: Record<string, ReturnType<typeof JSON.parse>>, operation?: "create" | "update" | "destroy", resource: ReturnType<typeof JSON.parse>, syncType?: string}} QueueArgs */

/**
 * Defers authoritative conflict application until Velocious releases the
 * replay operation. AwesomeTasks uses one SQLite connection per captured
 * project, so applying through a second tenant operation from inside replay
 * would otherwise wait behind itself.
 */
export default class ProjectSyncClient extends SyncClient {
  /**
   * Creates the single-connection application adapter.
   * @param {ProjectSyncClientOptions} options Client and durability options.
   * @throws {Error} When the durability drain is missing.
   */
  constructor(options) {
    const {flushMutationLog, flushAppOfflineQueue, ...syncClientOptions} = options

    super(syncClientOptions)
    if (typeof flushMutationLog !== "function") throw new Error("ProjectSyncClient requires a mutation-log durability drain")
    this.flushMutationLog = flushMutationLog
    this.flushAppOfflineQueue = typeof flushAppOfflineQueue === "function" ? flushAppOfflineQueue : null
    /** @type {Map<string, ConflictApply>} */
    this.deferredConflictApplies = new Map()
  }

  /**
   * Captures the framework conflict apply without copying or transforming its payload.
   * @param {ConflictApply} args Conflict application owned by the base client.
   * @returns {Promise<void>} Capture completion.
   */
  async applyConflictReplayResult(args) {
    this.deferredConflictApplies.set(conflictApplyKey(args), args)
  }

  /**
   * Returns only after every queued intent/write is durable in the project replica.
   * @param {QueueArgs} args Framework queue arguments.
   * @returns {Promise<ReturnType<typeof JSON.parse> | LocalMutationLogRecord>} Durable queued intent.
   */
  async queue(args) {
    const record = await super.queue(args)

    await this.flushMutationLog()

    return record
  }

  /**
   * Replays first, then applies authoritative conflict rows through fresh tenant operations.
   * @param {AbortSignal} signal Lifecycle cancellation signal.
   * @returns {Promise<void>} Replay and deferred-apply completion.
   * @rejects {Error} When replay, cancellation, or authoritative apply fails.
   */
  async _replayPending(signal) {
    await super._replayPending(signal)

    // Online-gated: the coordinator only reaches replay when online. Drain the
    // app-level offline queue for this replica through the framework's ordered
    // conflict-tracked replay (dependency order + conflict/rebase).
    if (this.flushAppOfflineQueue) await this.flushAppOfflineQueue()

    for (const [key, args] of this.deferredConflictApplies) {
      if (signal.aborted) throw signal.reason
      await super.applyConflictReplayResult(args)
      this.deferredConflictApplies.delete(key)
    }
  }

  /**
   * Re-applies persisted server authority when a prior generation stopped
   * after recording a conflict but before the single-connection deferred apply.
   * @param {AbortSignal} signal Owning coordinator generation.
   * @returns {Promise<void>} Recovery apply completion.
   * @rejects {Error} When persisted conflict metadata is malformed or apply fails.
   */
  async applyStoredConflictAuthority(signal) {
    for (const [resourceType, resourceConfig] of Object.entries(this.config.resources)) {
      const mutationLog = resourceConfig.conflictTracking?.mutationLog

      if (!mutationLog) continue
      for (const record of await mutationLog.records()) {
        if (record.status !== "conflict" || record.mutation.model !== resourceType) continue
        if (signal.aborted) throw signal.reason
        const syncResult = record.syncResult

        if (!syncResult || syncResult.syncState !== "conflict") {
          throw new Error(`Persisted ${resourceType} conflict ${record.id} has no conflict replay result`)
        }
        await super.applyConflictReplayResult({record, resourceType, result: /** @type {SyncReplayItem} */ (syncResult)})
      }
    }
  }

  /**
   * Clears generation-bound deferred applies after base-client quiescence.
   * @param {SyncClientStopOptions} [options] Base stop options.
   * @returns {Promise<void>} Stop completion.
   */
  async stop(options) {
    try {
      await super.stop(options)
    } finally {
      this.deferredConflictApplies.clear()
    }
  }
}

/**
 * Keys one deferred server-authority apply without retaining payload contents.
 * @param {ConflictApply} args Conflict application.
 * @returns {string} Stable resource-record key.
 * @throws {Error} When the durable mutation record has no identity.
 */
function conflictApplyKey({record, resourceType}) {
  if (typeof record.id !== "string" || record.id.length === 0) {
    throw new Error(`Deferred ${resourceType} conflict apply requires a mutation-log record id`)
  }

  return `${resourceType}:${record.id}`
}
